import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchCustomFieldValuesCacheKey,
  FetchCacheCustomFieldValuesFilters,
  FetchCustomFieldValuesSort,
  FetchCustomFieldValuesPage,
  FetchCustomFieldValuesLimit,
  FetchCustomFieldValuesGqlQuery
} from '../../customFieldValuesTypes';
import { FetchCacheCustomFieldValuesQueryResponse } from '../../queries/fetchCacheCustomFieldValues.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_CUSTOM_FIELD_FILTERS,
  INITIAL_CUSTOM_FIELD_LIMIT,
  INITIAL_CUSTOM_FIELD_PAGE,
  INITIAL_CUSTOM_FIELD_SORT
} from '../../customFieldValuesConstants';

interface CustomFieldValuesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface CustomFieldValuesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface CustomFieldValuesDefaultOptions {
  action?: string;
  cacheKey: FetchCustomFieldValuesCacheKey;
  query: FetchCustomFieldValuesGqlQuery;
  initialFilters?: FetchCacheCustomFieldValuesFilters;
  initialSort?: FetchCustomFieldValuesSort;
  initialPage?: FetchCustomFieldValuesPage;
  initialLimit?: FetchCustomFieldValuesLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type CustomFieldValuesOptions = CustomFieldValuesDefaultOptions &
  (
    | CustomFieldValuesWithPrefetchItemOptions
    | CustomFieldValuesWithoutPrefetchItemOptions
  );

const scope = 'customFieldValues';

function useCachePaginatedCustomFieldValues<
  FetchCustomFieldValuesType = FetchCacheCustomFieldValuesQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_CUSTOM_FIELD_FILTERS,
  initialSort = INITIAL_CUSTOM_FIELD_SORT,
  initialPage = INITIAL_CUSTOM_FIELD_PAGE,
  initialLimit = INITIAL_CUSTOM_FIELD_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: CustomFieldValuesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchCustomFieldValuesType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    customFieldValuesData: data,
    customFieldValues: items,
    customFieldValuesError: itemsError,
    customFieldValuesErrorMessage: itemsErrorMessage,
    customFieldValuesTotalCount: itemsTotalCount,
    customFieldValuesFetched: isFetched,
    customFieldValuesLoading: isLoading,
    customFieldValuesIsPlaceholderData: isPlaceholderData,
    customFieldValuesFilters: currentFilters,
    customFieldValuesSort: currentSort,
    customFieldValuesPage: currentPage,
    customFieldValuesLimit: currentLimit,
    updateCustomFieldValueCache: updateItemCache,
    updateCustomFieldValuesCache: updateItemsCache,
    filterCustomFieldValues: filterItems,
    changeCustomFieldValuesFilters: changeItemsFilters,
    clearCustomFieldValuesFilters: clearItemsFilters,
    sortCustomFieldValues: sortItems,
    paginateCustomFieldValues: paginateItems,
    limitCustomFieldValues: limitItems,
    prefetchCustomFieldValues: prefetchItems,
    prefetchCustomFieldValue: prefetchItem
  };
}

export default useCachePaginatedCustomFieldValues;
